// src/components/templates/RequestsTemplate.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RequestsHeader from '../requests/RequestsHeader';
import RequestForm from '../requests/RequestForm';
import OfferCard from '../requests/OfferCard';
import Loader from '../requests/Loader';
import ProviderView from '../requests/ProviderView';
import UserDashboard from '../requests/UserDashboard';
import { AuthModal } from '../auth/AuthModal';
import { ProfileModal } from '../shared/ProfileModal';
import { requestsService } from '../../services/requestsService';

const AppView = {
  HOME: 'HOME',
  RESULTS: 'RESULTS',
  PROVIDER: 'PROVIDER',
  USER_DASHBOARD: 'USER_DASHBOARD',
};

const RequestsTemplate = ({ bookId, bookData, user }) => {
  const navigate = useNavigate();
  
  const [view, setView] = useState(AppView.HOME);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offers, setOffers] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [discovery, setDiscovery] = useState(null);
  const [activeMissionId, setActiveMissionId] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  const [currentRequest, setCurrentRequest] = useState(null);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [userRequests, setUserRequests] = useState([]);
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  const scrollContainerRef = useRef(null);

  const isOwner = user && bookData && user.uid === bookData.ownerId;

  // Initialize Theme
  useEffect(() => {
    const savedTheme = localStorage.getItem(`requests-theme-${bookId}`);
    const configuredTheme = bookData?.styling?.theme;

    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
      return;
    }

    if (configuredTheme === 'light') {
      setIsDarkMode(false);
      return;
    }

    setIsDarkMode(true);
  }, [bookId, bookData]);

  // Replace the commented useEffect in RequestsTemplate with this:
  useEffect(() => {
    const loadUserRequests = async () => {
      if (!user?.uid || !bookId) return;
      
      try {
        const requests = await requestsService.getUserRequests(bookId, user.uid);
        setUserRequests(requests);
      } catch (error) {
        console.error('Failed to load user requests:', error);
      }
    };
    
    loadUserRequests();
  }, [bookId, user]);

  console.log('RequestsTemplate userRequests:', userRequests);

  // Apply theme changes
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    localStorage.setItem(`requests-theme-${bookId}`, isDarkMode ? 'dark' : 'light');
  }, [isDarkMode, bookId]);

  // Apply custom styling
  useEffect(() => {
    if (!bookData?.styling) return;

    const root = document.documentElement;
    const styling = bookData.styling;

    if (styling.primaryColor) root.style.setProperty('--color-primary', styling.primaryColor);
    if (styling.accentColor) root.style.setProperty('--color-accent', styling.accentColor);
    if (styling.backgroundColor) root.style.setProperty('--color-background', styling.backgroundColor);
    if (styling.textColor) root.style.setProperty('--color-text', styling.textColor);
    if (styling.fontFamily) root.style.setProperty('--font-family', styling.fontFamily);
  }, [bookData]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const handleRequestSubmit = async (data) => {
    if (!user?.uid) {
      setShowAuthModal(true);
      return;
    }

    setLoading(true);

    try {
      const newRequestId = await requestsService.createRequest(
        bookId,
        user?.uid ?? null,
        data
      );

      const createdRequest = {
        id: newRequestId,
        bookId,
        userId: user?.uid ?? null,
        ...data,
        status: 'open',
        externalDiscovery: {
          status: 'idle',
          externalLeadCount: 0,
          internalMatchCount: 0,
        existingOfferCount: 0
        },
        workflowStage: 'submitted',
        executionStage: 'awaiting_offer_approval',
        vertical: data.vertical,
        demandProfile: data.demandProfile,
        verticalData: data.verticalData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      setRequestId(newRequestId);
      setActiveMissionId(newRequestId);
      setCurrentRequestId(newRequestId);
      setCurrentRequest(createdRequest);

      if (user?.uid) {
        setUserRequests((prev) => [createdRequest, ...prev.filter((request) => request.id !== newRequestId)]);
      }

      const missionResult = await requestsService.launchMission(
        bookId,
        newRequestId
      );

      setOffers(missionResult?.offers || []);
      setShortlist(missionResult?.shortlist || []);
      setDiscovery(missionResult?.discovery || null);
      setCurrentRequest({
        ...createdRequest,
        matchingSummary: missionResult?.matchingSummary || null,
        workflowStage: missionResult?.request?.workflowStage || 'matching',
        executionStage: missionResult?.request?.executionStage || 'awaiting_offer_approval'
      });
      setView(AppView.USER_DASHBOARD);
    } catch (err) {
      console.error("Failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOffer = async (offer) => {
    if (!currentRequestId) return;

    try {
      const result = await requestsService.approveMissionOffer(bookId, currentRequestId, offer.id);
      
      const updatedRequest = {
        ...currentRequest,
        id: currentRequestId,
        status: 'IN_PROGRESS',
        workflowStage: 'offer_approved',
        executionStage: 'draft_created',
        acceptedOffer: offer,
        executionSummary: result?.execution || null
      };
      
      setCurrentRequest(updatedRequest);
      setUserRequests(prev => [updatedRequest, ...prev.filter((request) => request.id !== currentRequestId)]);
      setView(AppView.USER_DASHBOARD);
    } catch (err) {
      console.error("Failed to accept offer:", err);
    }
  };

  const handleUpdateRequest = async (updated) => {
    try {
      await requestsService.updateRequest(bookId, updated.id, updated);
      setUserRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
    } catch (err) {
      console.error("Failed to update request:", err);
    }
  };

  const handleLoadMore = async () => {
    if (!currentRequestId) return;
    setLoadingMore(true);

    try {
      const missionResult = await requestsService.refreshMissionShortlist(bookId, currentRequestId);
      setOffers(missionResult?.offers || []);
      setShortlist(missionResult?.shortlist || []);
      setDiscovery(missionResult?.discovery || null);
      
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error("Failed to load more:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleChangeView = (newView) => {
    setView(newView);
    if (newView === AppView.HOME) {
      setOffers([]);
      setShortlist([]);
      setDiscovery(null);
      setActiveMissionId(null);
      setCurrentRequest(null);
      setCurrentRequestId(null);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -340, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 340, behavior: 'smooth' });
    }
  };

  const primaryColor = bookData?.styling?.primaryColor || '#6366f1';

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${isDarkMode ? 'dark bg-[#0f172a]' : 'bg-slate-50'}`}>
      <RequestsHeader 
        currentView={view} 
        onChangeView={handleChangeView} 
        isDarkMode={isDarkMode} 
        onToggleTheme={toggleTheme}
        bookData={bookData}
        isOwner={isOwner}
        navigate={navigate}
        setShowAuthModal={setShowAuthModal}
        setShowProfileModal={setShowProfileModal}
      />

      <main className="flex-grow w-full">
        
        {view === AppView.PROVIDER && (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <ProviderView 
              isDarkMode={isDarkMode} 
              bookId={bookId} 
              bookData={bookData} 
              user={user}
            />
          </div>
        )}

        {view === AppView.USER_DASHBOARD && (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <UserDashboard 
              user={user}
              bookId={bookId}
              requests={userRequests} 
              onUpdateRequest={handleUpdateRequest}
              isDarkMode={isDarkMode}
              activeRequestId={activeMissionId}
            />
          </div>
        )}

        {view === AppView.HOME && (
          <>
            {!loading && (  
              <div className="flex flex-col items-center animate-fade-in pt-12 sm:pt-20 pb-12 px-4">
                <div className="text-center mb-10">
                  <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] mb-5 ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-800/60 text-slate-300'
                      : 'border-slate-200 bg-white text-slate-600'
                  }`}>
                    <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                    Agent King
                  </div>
                  <h1 className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-4`}>
                    Stop searching. <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500 dark:from-indigo-400 dark:via-sky-400 dark:to-emerald-400">Say what you want.</span>
                  </h1>
                  <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-900'} max-w-3xl mx-auto`}>
                    Agent King turns intent into a structured request, finds suitable providers, and keeps optimizing until you get the best solution.
                  </p>
                </div>
                
                <RequestForm 
                  onSubmit={handleRequestSubmit} 
                  isSubmitting={loading}
                  isDarkMode={isDarkMode}
                  bookData={bookData}
                  placeholder={bookData?.requestsSettings?.requestPlaceholder}
                  user={user}
                  onRequireAuth={() => setShowAuthModal(true)}
                />
                
                <div className={`mt-20 w-full max-w-4xl border-t pt-12 ${
                  isDarkMode ? 'border-slate-800' : 'border-slate-200'
                }`}>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
                  {[
                    { number: '2 Agents', label: 'Request + Offer Intelligence' },
                    { number: '10k+', label: 'Mapped Providers' },
                    { number: '50k+', label: 'Outcomes Coordinated' },
                    { number: '< 5m', label: 'First Match Cycle' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className={`rounded-2xl border px-4 py-5 text-center shadow-sm transition-colors ${
                        isDarkMode
                          ? 'border-slate-800 bg-slate-900/70 shadow-slate-950/20'
                          : 'border-slate-200 bg-white shadow-slate-200/70'
                      }`}
                    >
                      <p className={`text-2xl sm:text-3xl font-bold ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        {stat.number}
                      </p>
                      <p className={`mt-1 text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {stat.label}
                      </p>
                    </div>
                  ))}
                  </div>
                </div>
              </div>
            )}
            
            {loading && (
              <div className="flex flex-col items-center justify-center min-h-[50vh]">
                <Loader />
              </div>
            )}
          </>
        )}

        {view === AppView.RESULTS && (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 max-w-7xl mx-auto">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                  Agent matches for: "{currentRequest?.description}"
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                  Showing {offers.length} live provider offers. Refresh to check for newly received responses.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => handleChangeView(AppView.HOME)}
                  className="text-sm font-medium hover:opacity-80 flex items-center gap-1 transition-opacity"
                  style={{ color: primaryColor }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 12"/>
                    <path d="M3 3v9h9"/>
                  </svg>
                  New Outcome
                </button>
              </div>
            </div>

            {(shortlist.length > 0 || offers.length > 0) ? (
              <div className="relative group max-w-full">
                {shortlist.length > 0 && (
                  <div className="mb-8 max-w-7xl mx-auto">
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">Top 3 Shortlist</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Ranked for fit, execution readiness, and provider quality.
                      </p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      {shortlist.map((offer, index) => (
                        <OfferCard
                          key={`shortlist-${offer.id}`}
                          offer={offer}
                          index={index}
                          onAccept={handleAcceptOffer}
                          isDarkMode={isDarkMode}
                          primaryColor={primaryColor}
                          showRank
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="absolute top-1/2 -translate-y-1/2 left-2 z-10">
                  <button 
                    onClick={scrollLeft}
                    className="p-3 rounded-full bg-white/90 dark:bg-slate-800/90 shadow-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:scale-110 transition-all focus:outline-none"
                    aria-label="Scroll left"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                </div>
                
                <div className="absolute top-1/2 -translate-y-1/2 right-2 z-10">
                  <button 
                    onClick={scrollRight}
                    className="p-3 rounded-full bg-white/90 dark:bg-slate-800/90 shadow-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:scale-110 transition-all focus:outline-none"
                    aria-label="Scroll right"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <div 
                  ref={scrollContainerRef}
                  className="flex overflow-x-auto gap-6 py-4 px-8 snap-x snap-mandatory scrollbar-hide pb-12"
                >
                  {offers.map((offer, index) => (
                    <div key={offer.id} className="min-w-[300px] md:min-w-[360px] max-w-[360px] snap-center flex-shrink-0">
                      <OfferCard 
                        offer={offer} 
                        index={index} 
                        onAccept={handleAcceptOffer}
                        isDarkMode={isDarkMode}
                        primaryColor={primaryColor}
                      />
                    </div>
                  ))}

                  <div className="min-w-[300px] md:min-w-[360px] max-w-[360px] snap-center flex-shrink-0 flex">
                    <div 
                      className="w-full bg-slate-100 dark:bg-slate-800/50 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center p-8 text-center hover:bg-slate-200 dark:hover:bg-slate-800 transition-all group cursor-pointer" 
                      onClick={handleLoadMore}
                    >
                      {loadingMore ? (
                        <>
                          <div 
                            className="w-10 h-10 border-4 rounded-full animate-spin mb-4"
                            style={{
                              borderColor: `${primaryColor}20`,
                              borderTopColor: primaryColor
                            }}
                          ></div>
                          <p className="font-bold" style={{ color: primaryColor }}>
                            Expanding the agent search...
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="w-16 h-16 bg-white dark:bg-slate-700 rounded-full flex items-center justify-center shadow-sm mb-4 group-hover:scale-110 transition-transform">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: primaryColor }}>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                            Refresh Matches
                          </h3>
                          <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
                            Check if more providers responded to this mission.
                          </p>
                          <button 
                            className="px-6 py-2 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
                            style={{ backgroundColor: primaryColor }}
                          >
                            Refresh Responses
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {discovery && (
                  <div className={`mt-6 rounded-2xl border p-5 ${
                    isDarkMode ? 'border-amber-900/40 bg-amber-900/10' : 'border-amber-200 bg-amber-50/80'
                  }`}>
                    <div className="text-sm font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      Discovery Status
                    </div>
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                      Status: {String(discovery.status || 'idle').replace(/_/g, ' ')}.
                      {typeof discovery.internalMatchCount === 'number' ? ` Internal matches: ${discovery.internalMatchCount}.` : ''}
                      {typeof discovery.externalLeadCount === 'number' ? ` External leads: ${discovery.externalLeadCount}.` : ''}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-center gap-2 mt-4">
                  <div className="h-1.5 w-24 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min(100, (offers.length / (offers.length + 5)) * 100)}%`,
                        backgroundColor: primaryColor
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 border-dashed max-w-4xl mx-auto">
                <p className="text-slate-500 dark:text-slate-400 text-lg">
                  No strong registered matches found yet. Agent King can extend discovery beyond the current provider network.
                </p>
                <div className="mt-4 flex flex-col items-center gap-3">
                  {user?.uid && (
                    <button
                      onClick={() => handleChangeView(AppView.USER_DASHBOARD)}
                      className="rounded-lg px-5 py-2.5 font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Open Mission Dashboard
                    </button>
                  )}
                  <button 
                    onClick={() => handleChangeView(AppView.HOME)} 
                    className="font-medium hover:opacity-80 transition-opacity"
                    style={{ color: primaryColor }}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          onClose={() => setShowProfileModal(false)}
          onSave={(updatedProfile) => {
            console.log('Profile updated:', updatedProfile);
            setShowProfileModal(false);
          }}
        />
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RequestsTemplate;
